import React, { useEffect, useState, createElement } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DollarSignIcon, PlusIcon, AlertTriangleIcon, CalendarIcon } from 'lucide-react';
import { calculateValuation, QuizData, ValuationResult } from '../utils/ValuationCalculator';
import { SchedulerEmbed } from './SchedulerEmbed';
import { sendToGoHighLevel } from '../utils/ghlIntegration';
import { getQuizData } from '../utils/quizDataPersistence';
// Mock data for preview mode
const PREVIEW_DATA: QuizData = {
  industry: 'dental',
  industryOther: '',
  isOwner: 'yes',
  state: 'TX',
  yearsInBusiness: '10',
  revenue: '3-5M',
  profit: '15-25',
  locations: '2-3',
  providers: '4-5',
  payerMix: '50-75-insurance',
  concentration: 'no',
  trend: 'up-10-20',
  timeline: '12-24m',
  ownerHours: '<20'
};
interface ResultsPageProps {
  preview?: boolean;
}
export function ResultsPage({
  preview = false
}: ResultsPageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState<ValuationResult | null>(null);
  // Determine if we're in preview mode based on URL path or prop
  const isPreviewMode = preview || location.pathname.includes('/preview');
  // Try to get form data from navigation state first, then fall back to localStorage
  // In preview mode, use mock data
  const formData = isPreviewMode ? {
    ...PREVIEW_DATA,
    firstName: 'Preview',
    email: 'preview@example.com'
  } : location.state as QuizData & {
    firstName: string;
    email: string;
  } || getQuizData() as any;
  // QUALIFICATION CHECK: $2M+ revenue = qualified for calendar
  const isQualified = ['1-3M', '3-5M', '5-10M', '10-20M'].includes(formData?.revenue || '');
  useEffect(() => {
    // In preview mode, skip the redirect check
    if (!isPreviewMode) {
      // If no quiz data available at all, redirect to start
      if (!formData || !formData.revenue) {
        console.warn('No quiz data found, redirecting to start');
        navigate('/');
        return;
      }
    }
    console.log(isPreviewMode ? 'Preview Mode - Results Viewed' : 'ResultsViewed', formData);
    // Calculate valuation from quiz data
    const calculatedResult = calculateValuation(formData as QuizData);
    setResult(calculatedResult);
    // Only send to GHL if NOT in preview mode and contact info is available
    if (!isPreviewMode && formData.email) {
      sendToGoHighLevel({
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        email: formData.email,
        phone: formData.mobile || '',
        smsConsent: formData.smsConsent || false,
        industry: formData.industry,
        industryOther: formData.industryOther,
        isOwner: formData.isOwner,
        state: formData.state,
        yearsInBusiness: formData.yearsInBusiness,
        revenue: formData.revenue,
        profit: formData.profit,
        locations: formData.locations,
        providers: formData.providers,
        ownerHours: formData.ownerHours,
        payerMix: formData.payerMix,
        concentration: formData.concentration,
        trend: formData.trend,
        timeline: formData.timeline,
        valuationLow: calculatedResult.low,
        valuationHigh: calculatedResult.high,
        multipleRange: `${Math.round(calculatedResult.multipleRange[0])}x-${Math.round(calculatedResult.multipleRange[1])}x`,
        qualityScore: calculatedResult.qualityScore,
        submittedAt: new Date().toISOString(),
        source: 'valuation_calculator_results'
      }).catch(err => {
        console.error('GHL results submission error (non-blocking):', err);
      });
    }
  }, [formData, navigate, isPreviewMode]);
  useEffect(() => {
    // Load Wistia scripts
    const script1 = document.createElement('script');
    script1.src = 'https://fast.wistia.com/player.js';
    script1.async = true;
    document.body.appendChild(script1);
    const script2 = document.createElement('script');
    script2.src = 'https://fast.wistia.com/embed/n4ae7z485x.js';
    script2.async = true;
    script2.type = 'module';
    document.body.appendChild(script2);
    return () => {
      document.body.removeChild(script1);
      document.body.removeChild(script2);
    };
  }, []);
  const scrollToCalendar = () => {
    const calendarSection = document.getElementById('calendar-section');
    if (calendarSection) {
      calendarSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  if (!result) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-243">
        <div className="text-center" data-id="element-244">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" data-id="element-245"></div>
          <p className="text-gray-600" data-id="element-246">Calculating your valuation...</p>
        </div>
      </div>;
  }
  const formatCurrency = (value: number) => {
    // Round to nearest hundred thousand to avoid overly precise numbers
    const roundedValue = Math.round(value / 100000) * 100000;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(roundedValue);
  };
  const showScheduler = formData.isOwner === 'yes' && isQualified;
  const getKeyManRiskExplanation = () => {
    if (result.keyManRisk === 'High') {
      return 'Because you handle most patient visits, your value is reduced to reflect owner dependency. Training associates and reducing your clinical hours could add significant value.';
    } else if (result.keyManRisk === 'Moderate') {
      return 'Your clinic has some owner dependency. Delegating more patient care to associates could increase your multiple.';
    } else {
      return 'Your clinic operates well without heavy owner involvement, which increases its value to buyers.';
    }
  };
  return <div className="min-h-screen bg-white py-6 md:py-12 px-4" data-id="element-247">
      {/* Logo */}
      <div className="max-w-4xl mx-auto mb-6" data-id="element-248">
        <div className="animate-fade-in" data-id="element-249">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qmxPrQcAoJQeBtBGYhM9D6/516lion.png" alt="516 Partners Logo" className="h-16 md:h-24 mx-auto" data-id="element-250" />
        </div>
      </div>

      {/* Preview Mode Banner */}
      {isPreviewMode && <div className="max-w-4xl mx-auto mb-4" data-id="element-251">
          <div className="bg-yellow-100 border-2 border-yellow-500 p-4 text-center" data-id="element-252">
            <p className="text-yellow-900 font-semibold" data-id="element-253">
              📋 PREVIEW MODE - This is a sample results page with mock data.
              Changes made here will appear on the live results page.
            </p>
          </div>
        </div>}

      <div className="max-w-4xl mx-auto space-y-8 md:space-y-12" data-id="element-254">
        {/* Results Header */}
        <div className="bg-white border border-gray-200 p-4 sm:p-6 md:p-8 lg:p-12 animate-fade-in-scale" data-id="element-255">
          <div className="text-center mb-4 animate-fade-in" data-id="element-256">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-2 leading-tight" data-id="element-257">
              Congratulations! You Qualify for a Complete Exit Readiness MRI
            </h1>
            <p className="text-base sm:text-lg font-bold text-gray-600" data-id="element-258">
              Step 1: Watch the video to learn more
            </p>
          </div>

          {/* Wistia Video */}
          <div className="mb-8 md:mb-12 animate-stagger-in animate-delay-100" data-id="element-259">
            <style dangerouslySetInnerHTML={{
            __html: `
                  wistia-player[media-id='n4ae7z485x']:not(:defined) { 
                    background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/n4ae7z485x/swatch'); 
                    display: block; 
                    filter: blur(5px); 
                    padding-top: 56.25%; 
                  }
                `
          }} data-id="element-260" />
            <wistia-player media-id="n4ae7z485x" aspect="1.7777777777777777" style={{
            width: '100%',
            display: 'block'
          }} data-id="element-261" />
          </div>

          {/* Step 2 Header & Calendar - CONDITIONAL */}
          {showScheduler ? <div className="mb-8 md:mb-12 animate-stagger-in animate-delay-200" data-id="element-262">
              <div className="mb-6 text-center" data-id="element-263">
                <h2 className="text-base sm:text-lg font-bold text-gray-600" data-id="element-264">
                  Step 2: Book your call
                </h2>
              </div>

              {/* Calendar Embed - Directly under Step 2 */}
              <div className="bg-white mb-8" data-id="element-265">
                <iframe src="https://api.leadconnectorhq.com/widget/booking/ul1jgYvunxHt2TjeNpFB" style={{
              width: '100%',
              border: 'none',
              minHeight: '800px',
              height: '800px'
            }} scrolling="yes" id="ul1jgYvunxHt2TjeNpFB_1762398344212" title="Book Your Call" data-id="element-266" />
              </div>

              {/* Credibility Block */}
              <div className="bg-gray-50 border border-gray-300 p-6 sm:p-8" data-id="element-267">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4" data-id="element-268">
                  Who's actually doing your Exit MRI?
                </h3>
                <p className="text-sm sm:text-base text-gray-700 mb-6" data-id="element-269">
                  Your clinic isn't getting run through a generic template. Your
                  review is led by:
                </p>
                <div className="space-y-4 mb-6" data-id="element-270">
                  <div className="flex items-start space-x-3" data-id="element-271">
                    <span className="text-gray-900 font-bold flex-shrink-0 mt-1" data-id="element-272">
                      •
                    </span>
                    <p className="text-sm sm:text-base text-gray-700" data-id="element-273">
                      <strong data-id="element-274">Ex–EY-Parthenon M&A advisor</strong> who's worked
                      on buy‑side and sell‑side diligence for PE and strategic
                      healthcare deals.
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-275">
                    <span className="text-gray-900 font-bold flex-shrink-0 mt-1" data-id="element-276">
                      •
                    </span>
                    <p className="text-sm sm:text-base text-gray-700" data-id="element-277">
                      <strong data-id="element-278">Former Senior Financial Analyst</strong> for a
                      multi‑site health center who's built budgets, pro formas,
                      and expansion plans for new clinics and service lines.
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-279">
                    <span className="text-gray-900 font-bold flex-shrink-0 mt-1" data-id="element-280">
                      •
                    </span>
                    <p className="text-sm sm:text-base text-gray-700" data-id="element-281">
                      <strong data-id="element-282">
                        National healthcare data & provider architecture
                        consultant
                      </strong>{' '}
                      who's advised systems like UnitedHealth, Kaiser, Yale,
                      Harvard and major payers on provider, location, and
                      reimbursement data.
                    </p>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-gray-900 font-medium" data-id="element-283">
                  In plain English: the same level of financial, operational,
                  and data scrutiny buyers use <em data-id="element-284">ON</em> you is now working{' '}
                  <em data-id="element-285">FOR</em> you.
                </p>
              </div>
            </div> : <div className="mb-8 md:mb-12 animate-stagger-in animate-delay-200" data-id="element-286">
              <div className="bg-white border border-gray-200 p-6 sm:p-8 text-center" data-id="element-287">
                <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-4" data-id="element-288">
                  Want to Discuss Your Results?
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-6 max-w-2xl mx-auto" data-id="element-289">
                  Our review calls are reserved for clinics doing $2M+ in annual
                  revenue. If you're approaching that threshold or have
                  questions about growing to that level, reach out to us
                  directly.
                </p>
                <div className="space-y-4" data-id="element-290">
                  <p className="text-gray-900 font-medium text-base sm:text-lg" data-id="element-291">
                    Email us at{' '}
                    <a href="mailto:ivan@516partners.com" className="underline hover:text-yellow-600 transition-colors" data-id="element-292">
                      ivan@516partners.com
                    </a>
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-293">
                    We're here to help you understand your valuation and plan
                    your path forward.
                  </p>
                </div>
              </div>
            </div>}

          {/* Valuation Range */}
          <div className="bg-gray-50 border-2 border-gray-900 p-6 sm:p-8 mb-6 md:mb-8 animate-stagger-in animate-delay-300" data-id="element-294">
            <p className="text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide mb-2 text-center" data-id="element-295">
              Your Clinic's Range
            </p>
            <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 text-center whitespace-nowrap" data-id="element-296">
              {formatCurrency(result.low)} – {formatCurrency(result.high)}
            </div>
            <p className="text-xs sm:text-sm text-gray-600 text-center" data-id="element-297">
              Based on your answers. Not an appraisal.
            </p>
          </div>

          {/* Key Man Risk Alert */}
          {result.keyManRisk === 'High' && <div className="mb-6 md:mb-8 p-4 sm:p-6 bg-yellow-50 border border-yellow-600 animate-stagger-in animate-delay-400" data-id="element-298">
              <div className="flex items-start space-x-3" data-id="element-299">
                <AlertTriangleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 flex-shrink-0 mt-1" data-id="element-300" />
                <div data-id="element-301">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base" data-id="element-302">
                    Owner Dependency Impact
                  </h3>
                  <p className="text-gray-700 text-sm sm:text-base" data-id="element-303">
                    {getKeyManRiskExplanation()}
                  </p>
                </div>
              </div>
            </div>}

          {/* How We Calculated Your Range */}
          <div className="mb-6 md:mb-8 animate-stagger-in animate-delay-500" data-id="element-304">
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-2 text-center" data-id="element-305">
              How It Was Calculated...
            </h2>
            <p className="text-sm text-gray-600 text-center mb-6" data-id="element-306">
              These are extremely rough estimates, not exact numbers.
            </p>

            <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6" data-id="element-307">
              {/* EBITDA */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200 gap-4 animate-stagger-in animate-delay-100" data-id="element-308">
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0" data-id="element-309">
                  <DollarSignIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0" data-id="element-310" />
                  <span className="text-gray-900 font-medium text-sm sm:text-base truncate" data-id="element-311">
                    Annual Profit (EBITDA)
                  </span>
                </div>
                <span className="text-gray-900 font-bold text-sm sm:text-base whitespace-nowrap" data-id="element-312">
                  {formatCurrency(result.ebitda)}
                </span>
              </div>

              {/* Multiple */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200 gap-4 animate-stagger-in animate-delay-200" data-id="element-313">
                <span className="text-gray-900 font-medium text-sm sm:text-base min-w-0 truncate" data-id="element-314">
                  Market Multiple Applied
                </span>
                <span className="text-gray-900 font-bold text-sm sm:text-base whitespace-nowrap" data-id="element-315">
                  {Math.round(result.multipleRange[0])}x–
                  {Math.round(result.multipleRange[1])}x
                </span>
              </div>
            </div>
          </div>

          {/* Value Unlocks */}
          <div className="mb-6 md:mb-8 animate-stagger-in animate-delay-600" data-id="element-316">
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-4" data-id="element-317">
              Quick wins we see next:
            </h2>
            <div className="space-y-3" data-id="element-318">
              <div className="flex items-start space-x-3 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-100" data-id="element-319">
                <span className="text-gray-900 font-bold flex-shrink-0" data-id="element-320">•</span>
                <p className="text-gray-900 text-sm sm:text-base" data-id="element-321">
                  Find hidden expenses that can increase your value
                </p>
              </div>
              <div className="flex items-start space-x-3 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-200" data-id="element-322">
                <span className="text-gray-900 font-bold flex-shrink-0" data-id="element-323">•</span>
                <p className="text-gray-900 text-sm sm:text-base" data-id="element-324">
                  Reduce dependence on any single insurance or referral source
                </p>
              </div>
              <div className="flex items-start space-x-3 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-300" data-id="element-325">
                <span className="text-gray-900 font-bold flex-shrink-0" data-id="element-326">•</span>
                <p className="text-gray-900 text-sm sm:text-base" data-id="element-327">
                  Improve profit margins in ways buyers care about
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}